(() => {
  if (customElements.get('sg-collection-gallery')) return;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  class CollectionGallery extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.link = this.querySelector('.sg-card-gallery__link');
      this.template = this.querySelector('template[data-sg-gallery-photos]');
      if (!this.link) return;
      this.photos = [...this.link.querySelectorAll('img')];
      if (!this.template && this.photos.length < 2) return;
      this.events = new AbortController();
      this.dots = [...this.querySelectorAll('[data-photo]')];
      this.pauseButton = this.querySelector('.sg-card-gallery__pause');
      this.compact = this.hasAttribute('data-compact');
      this.index = 0;
      this.pending = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.visible = true;
      this.swiped = false;
      this.touchMode = false;
      this.interval = 1500;
      const signal = this.events.signal;
      const owner = this.closest('.sg-shop-all-card, .sg-product-card, .sg-best-sellers-card, .sg-product-carousel__card') || this;
      owner.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this.touchMode = false;
        this.hovered = true;
        this.warm();
        this.schedule(350);
      }, { signal });
      owner.addEventListener('pointerleave', event => {
        if (event.pointerType === 'touch') return;
        this.hovered = this.focused = false;
        this.reset();
      }, { signal });
      owner.addEventListener('focusin', event => {
        if (event.target === this.link || this.contains(event.target)) {
          this.focused = true;
          this.warm();
          this.schedule(this.interval);
        }
      }, { signal });
      owner.addEventListener('focusout', event => {
        if (!owner.contains(event.relatedTarget)) {
          this.focused = false;
          if (!this.hovered) this.reset();
        }
      }, { signal });
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
        this.show(index);
        this.schedule(this.interval);
      }, { signal }));
      this.pauseButton?.addEventListener('click', () => {
        this.setPaused(!this.paused);
        this.schedule(this.interval);
      }, { signal });
      this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.touchMode = false;
        this.focused = true;
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.dots.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.show(index);
        this.schedule(this.interval);
      }, { signal });
      this.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch') return;
        this.touchMode = true;
        this.touchStart = { x: event.clientX, y: event.clientY };
        this.swiped = false;
        this.stop();
        if (this.compact) return;
        this.warm();
      }, { signal });
      this.addEventListener('pointerup', event => {
        if (this.compact || !this.touchStart || event.pointerType !== 'touch') return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 35 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.swiped = true;
          this.show(this.index + (dx < 0 ? 1 : -1));
        }
      }, { signal });
      this.addEventListener('pointercancel', () => { this.touchStart = null; }, { signal });
      this.addEventListener('click', event => {
        if (this.swiped) {
          event.preventDefault();
          this.swiped = false;
        }
      }, { capture: true, signal });
      document.addEventListener('visibilitychange', () => this.schedule(this.interval), { signal });
      reducedMotion.addEventListener('change', () => this.schedule(this.interval), { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.schedule(this.interval);
        else this.reset();
      });
      this.observer.observe(this);
      this.querySelector('.sg-card-gallery__controls').hidden = false;
      this.reset();
      this.dataset.ready = 'true';
    }
    disconnectedCallback() {
      this.stop();
      this.pending += 1;
      this.observer?.disconnect();
      this.events?.abort();
      this.events = null;
    }
    warm() {
      if (!this.template) return;
      this.link.append(this.template.content.cloneNode(true));
      this.template.remove();
      this.template = null;
      this.photos = [...this.link.querySelectorAll('img')];
    }
    stop() {
      clearTimeout(this.timer);
      this.timer = null;
    }
    canPlay() {
      return this.isConnected && this.visible && !document.hidden && !reducedMotion.matches && !this.touchMode && !this.paused && (this.hovered || this.focused);
    }
    schedule(delay) {
      this.stop();
      if (!this.canPlay()) return;
      this.timer = setTimeout(async () => {
        if (!this.canPlay()) return;
        await this.show(this.index + 1);
        this.schedule(this.interval);
      }, delay);
    }
    async show(index) {
      this.warm();
      const next = (index + this.photos.length) % this.photos.length;
      const photo = this.photos[next];
      const pending = ++this.pending;
      try { await photo.decode(); } catch { return; }
      if (pending !== this.pending || !this.isConnected) return;
      this.photos.forEach((image, i) => {
        image.classList.toggle('is-active', i === next);
        image.setAttribute('aria-hidden', String(i !== next));
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === next)));
      this.index = next;
      this.dataset.activePhoto = String(next + 1);
    }
    setPaused(paused) {
      this.paused = paused;
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.resumeLabel : this.pauseButton.dataset.pauseLabel);
      this.pauseButton.firstElementChild.textContent = '';
    }
    reset() {
      this.stop();
      this.pending += 1;
      this.index = 0;
      this.setPaused(false);
      this.photos.forEach((image, i) => {
        image.classList.toggle('is-active', i === 0);
        image.setAttribute('aria-hidden', String(i !== 0));
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === 0)));
      this.dataset.activePhoto = '1';
    }
  }
  customElements.define('sg-collection-gallery', CollectionGallery);
})();
